import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/productionLogger';

interface QueuedRequest {
  resolve: (value: any) => void;
  reject: (error: any) => void;
}

interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
}

/**
 * Service centralisé pour gérer l'authentification et les erreurs JWT
 * Empêche les boucles infinies et gère le refresh automatique
 */
export class AuthInterceptorService {
  private static instance: AuthInterceptorService;
  
  // État du refresh
  private isRefreshing = false;
  private failedQueue: QueuedRequest[] = [];
  
  // Circuit breaker
  private consecutiveFailures = 0;
  private lastFailureTime = 0;
  private circuitBreakerThreshold = 5;
  private circuitBreakerTimeout = 30000; // 30 secondes
  
  // Configuration retry
  private retryConfig: RetryConfig = {
    maxAttempts: 3,
    baseDelay: 1000, // 1 seconde
    maxDelay: 8000   // 8 secondes max
  };

  private constructor() {
    this.setupAuthListener();
  }

  public static getInstance(): AuthInterceptorService {
    if (!AuthInterceptorService.instance) {
      AuthInterceptorService.instance = new AuthInterceptorService();
    }
    return AuthInterceptorService.instance;
  }

  /**
   * Écouter les événements d'authentification Supabase
   */
  private setupAuthListener(): void {
    supabase.auth.onAuthStateChange((event, _session) => {
      if (event === 'TOKEN_REFRESHED') {
        logger.debug('🔄 Token refreshed successfully');
        this.resetCircuitBreaker();
        this.processFailedQueue(null);
      } else if (event === 'SIGNED_OUT') {
        logger.debug('👋 User signed out');
        this.clearFailedQueue();
        this.resetCircuitBreaker();
      }
    });
  }

  /**
   * Vérifier si le circuit breaker est ouvert
   */
  private isCircuitBreakerOpen(): boolean {
    if (this.consecutiveFailures < this.circuitBreakerThreshold) {
      return false;
    }

    const timeSinceLastFailure = Date.now() - this.lastFailureTime;
    if (timeSinceLastFailure > this.circuitBreakerTimeout) {
      logger.info('🔄 Circuit breaker timeout expired, resetting');
      this.resetCircuitBreaker();
      return false;
    }

    return true;
  }

  /**
   * Réinitialiser le circuit breaker
   */
  private resetCircuitBreaker(): void {
    this.consecutiveFailures = 0;
    this.lastFailureTime = 0;
  }

  /**
   * Enregistrer un échec d'authentification
   */
  private recordAuthFailure(): void {
    this.consecutiveFailures++;
    this.lastFailureTime = Date.now();
    
    logger.warn('⚠️ Auth failure recorded', {
      consecutiveFailures: this.consecutiveFailures,
      circuitBreakerOpen: this.isCircuitBreakerOpen()
    });
  }

  /**
   * Calculer le délai de retry avec backoff exponentiel
   */
  private calculateRetryDelay(attempt: number): number {
    const delay = Math.min(
      this.retryConfig.baseDelay * Math.pow(2, attempt - 1),
      this.retryConfig.maxDelay
    );
    
    // Ajouter un peu de jitter pour éviter les thundering herds
    const jitter = Math.random() * 0.1 * delay;
    return delay + jitter;
  }

  /**
   * Traiter la queue des requêtes échouées
   */
  private processFailedQueue(error: Error | null): void {
    const queue = [...this.failedQueue];
    this.failedQueue = [];

    queue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(null); // Signal pour retry
      }
    });
  }

  /**
   * Vider la queue des requêtes échouées
   */
  private clearFailedQueue(): void {
    const queue = [...this.failedQueue];
    this.failedQueue = [];
    
    const signOutError = new Error('User signed out');
    queue.forEach(({ reject }) => reject(signOutError));
  }

  /**
   * Tenter de rafraîchir le token
   */
  private async attemptTokenRefresh(): Promise<boolean> {
    if (this.isRefreshing) {
      // Attendre que le refresh en cours se termine
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;
    
    try {
      logger.debug('🔄 Attempting token refresh');
      
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        throw error;
      }

      if (!data.session) {
        throw new Error('No session after refresh');
      }

      logger.info('✅ Token refresh successful');
      this.resetCircuitBreaker();
      this.processFailedQueue(null);
      return true;

    } catch (error) {
      logger.error('❌ Token refresh failed', error as Error);
      this.recordAuthFailure();
      
      // Si le refresh échoue, déconnecter l'utilisateur
      if (this.isCircuitBreakerOpen()) {
        logger.warn('🚨 Circuit breaker open, signing out user');
        await supabase.auth.signOut();
      }
      
      this.processFailedQueue(error as Error);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Détecter si une erreur est liée à l'authentification
   */
  public isAuthError(error: any): boolean {
    if (!error) return false;

    // Vérifier le message d'erreur
    const message = error.message?.toLowerCase() || '';
    const authMessages = [
      'jwt expired',
      'invalid jwt',
      'jwt malformed',
      'refresh_token_not_found',
      'invalid refresh token',
      'token has expired'
    ];

    if (authMessages.some(msg => message.includes(msg))) {
      return true;
    }

    // Vérifier le code de statut HTTP
    if (error.status === 401 || error.code === 401) {
      return true;
    }

    // Vérifier les codes d'erreur PostgREST
    const postgrestAuthCodes = ['PGRST301', 'PGRST302'];
    if (postgrestAuthCodes.includes(error.code)) {
      return true;
    }

    return false;
  }

  /**
   * Détecter si une erreur est liée au réseau
   */
  public isNetworkError(error: any): boolean {
    if (!error) return false;

    const message = error.message?.toLowerCase() || '';
    const networkMessages = [
      'network request failed',
      'fetch failed',
      'network error',
      'err_network_changed',
      'err_internet_disconnected',
      'connection refused'
    ];

    return networkMessages.some(msg => message.includes(msg));
  }

  /**
   * Intercepter et gérer une requête avec retry automatique
   */
  public async interceptRequest<T>(
    requestFn: () => Promise<T>,
    context: string = 'unknown'
  ): Promise<T> {
    // Vérifier le circuit breaker
    if (this.isCircuitBreakerOpen()) {
      const error = new Error('Circuit breaker is open - too many auth failures');
      logger.warn('🚨 Request blocked by circuit breaker', { context });
      throw error;
    }

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.retryConfig.maxAttempts; attempt++) {
      try {
        logger.debug('🔄 Attempting request', { context, attempt });
        
        const result = await requestFn();
        
        // Succès - réinitialiser le circuit breaker
        if (attempt > 1) {
          logger.info('✅ Request succeeded after retry', { context, attempt });
        }
        this.resetCircuitBreaker();
        
        return result;

      } catch (error: any) {
        lastError = error;
        
        logger.debug('❌ Request failed', error as Error);

        // Si c'est une erreur d'authentification
        if (this.isAuthError(error)) {
          logger.warn('🔐 Auth error detected, attempting token refresh');
          
          const refreshSuccess = await this.attemptTokenRefresh();
          
          if (!refreshSuccess) {
            // Le refresh a échoué, arrêter les tentatives
            logger.error('❌ Token refresh failed, stopping retries');
            break;
          }
          
          // Le refresh a réussi, continuer avec la prochaine tentative
          continue;
        }

        // Si c'est une erreur réseau et qu'il reste des tentatives
        if (this.isNetworkError(error) && attempt < this.retryConfig.maxAttempts) {
          const delay = this.calculateRetryDelay(attempt);
          logger.debug('🌐 Network error, retrying after delay', error as Error);
          
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        // Pour les autres erreurs, arrêter immédiatement
        if (!this.isAuthError(error) && !this.isNetworkError(error)) {
          logger.debug('🛑 Non-retryable error, stopping', error as Error);
          break;
        }

        // Si c'est la dernière tentative
        if (attempt === this.retryConfig.maxAttempts) {
          logger.error('❌ All retry attempts exhausted');
          break;
        }

        // Attendre avant la prochaine tentative
        const delay = this.calculateRetryDelay(attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    // Toutes les tentatives ont échoué
    throw lastError || new Error('Request failed after all retries');
  }

  /**
   * Obtenir les statistiques du service
   */
  public getStats() {
    return {
      isRefreshing: this.isRefreshing,
      queuedRequests: this.failedQueue.length,
      consecutiveFailures: this.consecutiveFailures,
      circuitBreakerOpen: this.isCircuitBreakerOpen(),
      lastFailureTime: this.lastFailureTime
    };
  }
}

// Export de l'instance singleton
export const authInterceptor = AuthInterceptorService.getInstance();